import {
    getAllSpotlightNavItems,
    getCreateActionItems,
    Spotlight,
} from '@thoughtspot/global-nav';
import { useTranslation } from '@thoughtspot/i18n';
import {
    canCreateOrEditConnections,
    getCurrentOrgId,
    isAdminTabVisible,
    isConvAssistEnabled,
} from '@thoughtspot/session-service';
import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthenticatedState } from '/@services/system/login-service';
import { useAppContext } from '../../contexts/global-app-context';
import { routePaths } from '../../routing/routes-util';
import { getSpotlightPathPrivilege } from '../admin/spotlight-path-privileges';
import {
    isAdminSpotlightItem,
    useAdminSpotlightItems,
} from '../admin/use-admin-spotlight-items';
import { useSpotlightFilter } from '../admin/use-spotlight-filter';
import {
    SPOTLIGHT_CREATE_COLLECTION_EVENT,
    SPOTLIGHT_CREATE_LIVEBOARD_EVENT,
} from './spotlight-create-modals.container';

declare const window: any;

export const SpotlightContainer: React.FC = () => {
    const { navService, sessionService } = useAppContext();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { isAuthenticated } = useAuthenticatedState();

    const registeredPaths = useMemo(
        () => new Set(Object.values(routePaths)),
        [],
    );
    const isPathEnabled = useCallback(
        (path: string): boolean =>
            getSpotlightPathPrivilege(path, registeredPaths),
        [registeredPaths, isAuthenticated],
    );

    const baseSpotlightItems = useMemo(() => {
        const tStr = t as (key: string) => string;
        const navItems = getAllSpotlightNavItems(isPathEnabled, tStr);
        const createActions = getCreateActionItems(
            routePaths,
            tStr,
            !!canCreateOrEditConnections(),
        );
        return [...navItems, ...createActions];
    }, [isPathEnabled, t]);

    const {
        adminSpotlightItems,
        handleAdminItemSelect,
    } = useAdminSpotlightItems(navService);

    const spotlightItems = useMemo(
        () => [...baseSpotlightItems, ...adminSpotlightItems],
        [baseSpotlightItems, adminSpotlightItems],
    );

    const {
        filterItems,
        filterItemsLoading,
        handleFilterSelect,
    } = useSpotlightFilter();

    const handleSpotlightSelect = useCallback(
        (item: {
            id: string;
            path?: string;
            action?: () => void;
            openModalOnNav?: boolean;
        }) => {
            if (item.id === 'action-create-liveboard') {
                window.dispatchEvent(
                    new CustomEvent(SPOTLIGHT_CREATE_LIVEBOARD_EVENT),
                );
            } else if (item.id === 'action-create-collection') {
                window.dispatchEvent(
                    new CustomEvent(SPOTLIGHT_CREATE_COLLECTION_EVENT),
                );
            } else if (isAdminSpotlightItem(item as any)) {
                handleAdminItemSelect(item as any);
            } else if (item.path && item.openModalOnNav) {
                navigate(item.path, { state: { openCreateModal: true } });
            } else if (item.path && navService?.goToUrl) {
                navService.goToUrl(item.path);
            } else if (item.action) {
                item.action();
            }
        },
        [handleAdminItemSelect, navigate, navService],
    );

    return (
        <Spotlight
            items={spotlightItems}
            navService={navService}
            onItemSelect={handleSpotlightSelect}
            onFilterSelect={handleFilterSelect}
            filterItems={filterItems}
            filterItemsLoading={filterItemsLoading}
            enableEurekaSearch
            eurekaSearchPath={routePaths.Eureka}
            showAdminItem={isAdminTabVisible()}
            showSpotterItem={isConvAssistEnabled()}
            spotterPath={routePaths.ConvAssist}
            isPathEnabled={isPathEnabled}
            currentPath={pathname}
            userId={sessionService.getUserGuid()}
            orgId={getCurrentOrgId()}
        />
    );
};
